import './utils/instrument';
import 'react-dates/initialize';
import * as Sentry from '@sentry/react';
import { ConfigProvider, message } from 'antd';
import 'antd/dist/antd.min.css';
import 'leaflet/dist/leaflet.css';
import React from 'react';
// React dates will be lazy loaded when needed
import ReactDOM from 'react-dom';
import { Helmet } from 'react-helmet';
import { BrowserRouter as Router } from 'react-router-dom';
import styled from 'styled-components';
import { BaseModalBackground, ModalProvider } from 'styled-react-modal';
import ApolloProvider from './components/ApolloProvider';
import CacheBuster from './components/CacheBuster';
import { ErrorFallback } from './components/ErrorBoundary';
import Layout from './components/Layout';
import ManualModeBanner from './components/ManualModeBanner';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import ServiceWorkerFixPrompt from './components/ServiceWorkerFixPrompt';
import GPSStatusIndicator from './components/GPSStatusIndicator';
import ScrollToTop from './components/ScrollToTop';
import FloatButton from './components/UploadFloatButton';
import PackageTypingToggleButton from './components/PackageTypingToggleButton';
import VersionCheck from './components/VersionCheck';
import { ContextProvider, useAuthContext } from './context';
import { isBatEmployee } from './utils/roles';
import './utils/memoryCleanup';
import './utils/draftOrderMonitor'; // Initialize draft order monitoring
import './utils/debugStorage'; // Debug utilities (dev only)
import './css/App.css';
import './styles/mobileOptimizations.css';
// Offline data synchronization hooks
import { useCommentsOfflineData } from './hooks/AsyncComments';
import { useCustomersOfflineData } from './hooks/AsyncCustomers';
import { usePeriodicImageUpdater } from './hooks/AsyncImages';
import { useProductsOfflineData } from './hooks/AsyncProducts';
import { useReportsOfflineData } from './hooks/AsyncReports';
import { useTasksOfflineData } from './hooks/AsyncTasks';
import { usePaymentOptionsOfflineData } from './hooks/usePaymentOptionsOfflineData';
import { useOpenItemsOfflineData } from './hooks/AsyncOpenItems';
import {
  getAntdLocale,
  getLanguage,
  i18n,
  initializeLocalization,
} from './localization';
import Routes from './routes';
import * as serviceWorker from './serviceWorker';
import deviceCapabilityDetector from './utils/deviceCapabilityDetector';
import persistentLogger from './utils/persistentConsoleLogger';
import { ensurePersistentStorage } from './utils/persistentStorage';

// Only enable persistent logging in development for debugging
if (process.env.NODE_ENV !== 'production') {
  persistentLogger.init().catch(console.error);
}

if (process.env.NODE_ENV === 'production') {
  window.addEventListener('error', (event) => {
    const error = event.error || new Error(event.message || 'Unknown error');
    Sentry.captureException(error, {
      contexts: {
        errorEvent: {
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (!event.reason) return;
    Sentry.captureException(event.reason, {
      contexts: {
        promiseRejection: {
          type: 'unhandledrejection',
          reason: String(event.reason),
        },
      },
    });
  });
}

deviceCapabilityDetector.logDeviceInfo();
const { tier, hardware } = deviceCapabilityDetector.deviceInfo;
if (tier === 'LOW_END' || hardware.isLowEndDevice) {
  document.body.classList.add('low-end-device');
  console.info('🔧 Low-end device optimizations activated');
} else {
  console.info(`Device tier: ${tier} - Standard optimizations active`);
}

// Messages config
message.config({
  top: 80,
  duration: 2,
  maxCount: 3,
});

const FadingBackground = styled(BaseModalBackground)`
  opacity: ${(props) => props.opacity};
  transition: opacity ease 200ms;
`;

const OfflineFull = () => {
  useCustomersOfflineData();
  useProductsOfflineData();
  useTasksOfflineData();
  useCommentsOfflineData();
  useReportsOfflineData();
  usePeriodicImageUpdater();
  usePaymentOptionsOfflineData();
  useOpenItemsOfflineData();
  return <></>;
};

const OfflineBat = () => {
  useCustomersOfflineData();
  return <></>;
};

const Offline = () => {
  const { authUser } = useAuthContext();
  return isBatEmployee(authUser) ? <OfflineBat /> : <OfflineFull />;
};

// This is the main application component
const App = () => {
  const [showUpdateNotification, setShowUpdateNotification] =
    React.useState(false);
  const [language, setLanguageState] = React.useState(getLanguage());

  React.useEffect(() => {
    const handleLanguageChanged = (nextLanguage) => {
      setLanguageState((currentLanguage) =>
        currentLanguage === nextLanguage ? currentLanguage : nextLanguage,
      );
    };

    i18n.on('languageChanged', handleLanguageChanged);

    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  const renderApp = (cacheInfo) => (
    <ConfigProvider locale={getAntdLocale(language)}>
      <ApolloProvider>
        <Sentry.ErrorBoundary
          fallback={({ error, componentStack, resetError }) => (
            <ErrorFallback
              error={error}
              componentStack={componentStack}
              resetError={resetError}
            />
          )}
          beforeCapture={(scope, error, componentStack) => {
            scope.setTag('errorBoundary', 'root');
            scope.setContext('componentStack', {
              stack: componentStack,
            });
          }}
          showDialog={false}
        >
          <Helmet>
            <meta name="viewport" content="width=device-width" />
          </Helmet>
          <ModalProvider backgroundComponent={FadingBackground}>
            <ContextProvider>
              <Router>
                <ManualModeBanner />
                <PWAInstallPrompt />
                <ServiceWorkerFixPrompt />
                <GPSStatusIndicator />
                <ScrollToTop />
                <Offline />
                <FloatButton />
                <PackageTypingToggleButton />

                <VersionCheck
                  newVersionAvailable={cacheInfo?.newVersionAvailable}
                  currentVersion={cacheInfo?.currentVersion}
                  latestVersion={cacheInfo?.latestVersion}
                  changelog={cacheInfo?.changelog}
                  onUpdate={cacheInfo?.refreshCacheAndReload}
                  onDismiss={() => setShowUpdateNotification(true)}
                  isDismissed={showUpdateNotification}
                />

                <Layout>
                  <Routes />
                </Layout>
              </Router>
            </ContextProvider>
          </ModalProvider>
        </Sentry.ErrorBoundary>
      </ApolloProvider>
    </ConfigProvider>
  );

  return (
    <CacheBuster manualApprove={true}>
      {(cacheInfo) => renderApp(cacheInfo)}
    </CacheBuster>
  );
};

function renderRoot() {
  ReactDOM.render(<App />, document.getElementById('root'));
}

if (typeof window !== 'undefined' && window.__msalesBootFallbackShown) {
  console.warn('[mSales] Boot fallback active — skipping React mount');
} else {
  // Mount on the bundled Bosnian catalog right away; the selected language is
  // loaded in the background and swapped in on the languageChanged event.
  renderRoot();
  initializeLocalization().catch((error) => {
    console.error('Failed to initialize localization.', error);
  });
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.register();

ensurePersistentStorage().then((granted) => {
  if (granted !== null) {
    Sentry.setTag('persistentStorage', granted ? 'granted' : 'denied');
  }
});
